import { type LegalDocument } from '@/modules/api';

export type LegalDocumentGroup = {
  /** The document whose name labels the visible checkbox (`displayAgree: true`). */
  anchor: LegalDocument;
  /**
   * Other documents in the same `checkboxGroup` that share the anchor's
   * checkbox — render their `name`s as inline links inside the anchor's
   * label. Sorted by `order` so link order is server-controlled.
   */
  linked: LegalDocument[];
};

// Groups the response by `checkboxGroup`. Each group surfaces one checkbox,
// labeled by its `displayAgree: true` document (anchor); the rest of the
// group's documents are linked inside the anchor's label. Groups with no
// anchor are dropped — there's nothing to render. Groups themselves are
// returned ordered by `checkboxGroup` for stable form layout across requests.
export function groupLegalDocuments(documents: LegalDocument[]): LegalDocumentGroup[] {
  const byGroup = new Map<number, LegalDocument[]>();
  for (const doc of documents) {
    const list = byGroup.get(doc.checkboxGroup);
    if (list) {
      list.push(doc);
    } else {
      byGroup.set(doc.checkboxGroup, [doc]);
    }
  }

  const groups: LegalDocumentGroup[] = [];
  for (const docs of byGroup.values()) {
    docs.sort((a, b) => a.order - b.order);
    const anchor = docs.find((d) => d.displayAgree);
    if (!anchor) continue;
    const linked = docs.filter((d) => d !== anchor);
    groups.push({ anchor, linked });
  }

  return groups.sort((a, b) => a.anchor.checkboxGroup - b.anchor.checkboxGroup);
}
